/** Shared customer availability copy. Null capacity means unlimited/unknown. */
export type Availability = {
  state: "available" | "low" | "sold_out";
  remaining: number | null;
  label: string | null;
};

export const LOW_AVAILABILITY_RATIO = 0.2;

export function availabilityFor(input: {
  remaining: number | null;
  capacity?: number | null;
  soldOut?: boolean;
  unit?: "seat" | "spot" | "item";
  lowStockThreshold?: number | null;
}): Availability {
  const remaining =
    input.remaining == null ? null : Math.max(0, input.remaining);
  if (input.soldOut || remaining === 0) {
    return { state: "sold_out", remaining: 0, label: "Sold out" };
  }
  const low =
    remaining != null &&
    (input.capacity != null && input.capacity > 0
      ? remaining / input.capacity <= LOW_AVAILABILITY_RATIO
      : remaining <= (input.lowStockThreshold ?? 2));
  const unit = input.unit ?? "seat";
  return {
    state: low ? "low" : "available",
    remaining,
    label: low
      ? `Only ${remaining} ${unit}${remaining === 1 ? "" : "s"} left`
      : null,
  };
}

/** Explicit sell-out survives cancellations and newly generated recurring dates. */
export function isManuallySoldOut(
  metadata: Record<string, unknown> | null | undefined,
): boolean {
  return metadata?.manuallySoldOut === true;
}

/**
 * One pass can no longer be bought: its own seats, or the occurrence's, are
 * fewer than one pass needs. The single definition behind both the
 * "Sold out" badge and the "from" price, so they can never disagree.
 */
export function isEventPassSoldOut(
  pass: { remaining: number | null; attendeesPerPass?: number },
  occurrenceRemaining?: number | null,
): boolean {
  const needed = pass.attendeesPerPass ?? 1;
  return (
    (pass.remaining != null && pass.remaining < needed) ||
    (occurrenceRemaining != null && occurrenceRemaining < needed)
  );
}

/**
 * The passes a "from" price may quote: the ones still on sale. A sold-out
 * cheap tier must not keep advertising "₹1,250 onwards" when the cheapest
 * thing anyone can buy is ₹1,500. When EVERY pass is gone the full list is
 * returned, so a sold-out card still shows what it cost next to its badge
 * instead of losing its price.
 */
export function passesQuotableForPrice<
  T extends { remaining: number | null; attendeesPerPass?: number },
>(passes: T[], occurrenceRemaining?: number | null): T[] {
  const onSale = passes.filter(
    (pass) => !isEventPassSoldOut(pass, occurrenceRemaining),
  );
  return onSale.length > 0 ? onSale : passes;
}

export function eventAvailability(occurrence: {
  remaining?: number | null;
  capacity?: number | null;
  passes?: Array<{
    isActive: boolean;
    remaining: number | null;
    capacity?: number | null;
    attendeesPerPass?: number;
  }>;
}): Availability {
  const passes = (occurrence.passes ?? []).filter((pass) => pass.isActive);
  const allFinite =
    passes.length > 0 && passes.every((pass) => pass.remaining != null);
  const passRemaining = allFinite
    ? passes.reduce((sum, pass) => sum + (pass.remaining ?? 0), 0)
    : null;
  const passCapacity =
    allFinite &&
    passes.every((pass) => pass.capacity != null && pass.capacity > 0)
      ? passes.reduce((sum, pass) => sum + (pass.capacity ?? 0), 0)
      : null;
  const remaining =
    occurrence.remaining == null
      ? passRemaining
      : passRemaining == null
        ? occurrence.remaining
        : Math.min(occurrence.remaining, passRemaining);
  const capacity =
    occurrence.remaining != null &&
    (passRemaining == null || occurrence.remaining <= passRemaining)
      ? occurrence.capacity
      : passCapacity;
  const soldOut =
    passes.length > 0 &&
    passes.every((pass) => isEventPassSoldOut(pass, occurrence.remaining));
  return availabilityFor({ remaining, capacity, soldOut });
}
